import { useDataProvider } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";

import {
  type EnrichmentEntity,
  enrichmentContentTypeOf,
} from "@/lib/tina/content-types";
import {
  type TracePointer,
  jobMatchesEntity,
  resolveStaticTracePointer,
} from "@/pages/tina/lib/entity-trace";
import type { PipelineItem } from "@/types/tina";

/**
 * Resolves a Mood entity to the Tina item worth linking to, in three tiers.
 *
 * 1. **`metadata` stamp** → the walk that CREATED the entity. Stable.
 * 2. **`enrichment.taskName`** → whatever LAST enriched it. Moves on re-enrich.
 * 3. **`GET /events?entity_id=`** → a live lookup of the entity's enrichment
 *    jobs, newest first.
 *
 * The order of 1 and 2 is the load-bearing part and is argued in
 * `lib/entity-trace.ts`. Tier 3 is why this is a hook at all: it is the only
 * tier that survives artist/venue `taskName`s destroyed before the
 * `.passthrough()` fix (those are not recoverable) and the only one that works
 * on the entire population created before the `metadata` stamp shipped, which
 * is not retroactive.
 *
 * **Tier 3 is deliberately defensive.** `entity_id` is a new orchestrator
 * parameter (PR #606). FastAPI *ignores* query parameters it does not declare
 * rather than rejecting them, so an orchestrator deployed without it answers
 * this request with an unfiltered page of unrelated rows — which would render a
 * confidently wrong trace. Every returned row is therefore re-checked against
 * the entity id client-side ({@link jobMatchesEntity}); on an older deploy
 * nothing matches and the caller simply gets no trace.
 */
export interface UseEntityTraceInput {
  entityType: EnrichmentEntity;
  entityId: string | null | undefined;
  /** The entity document's `metadata` block, whatever shape it has. */
  metadata?: unknown;
  /** `enrichment.taskName`, when the entity's enrichment carries one. */
  taskName?: string | null;
  /**
   * Skip the live lookup. Use it where a miss is expected and a request per
   * page view is not worth it — genre carries no `taskName` by design.
   */
  disableLookup?: boolean;
}

export interface UseEntityTraceResult {
  pointer: TracePointer | null;
  isLoading: boolean;
}

/** How many job rows to inspect before giving up on a match. */
const LOOKUP_PAGE_SIZE = 5;

export function useEntityTrace({
  entityType,
  entityId,
  metadata,
  taskName,
  disableLookup = false,
}: UseEntityTraceInput): UseEntityTraceResult {
  const { getList } = useDataProvider()("tina");

  const staticPointer = resolveStaticTracePointer({ metadata, taskName });
  const lookupEnabled = !staticPointer && !disableLookup && !!entityId;

  const lookup = useQuery({
    queryKey: ["tina", "entity-trace", entityType, entityId],
    enabled: lookupEnabled,
    staleTime: 60_000,
    // A missing `entity_id` parameter on the orchestrator is a deploy-order
    // condition, not a bug worth three retries per page view.
    retry: false,
    queryFn: async (): Promise<TracePointer | null> => {
      const response = await getList({
        resource: "tina-items",
        pagination: {
          currentPage: 1,
          pageSize: LOOKUP_PAGE_SIZE,
          mode: "server" as const,
        },
        filters: [
          { field: "entity_id", operator: "eq", value: entityId! },
          // Both filters are sent: `entity_id` alone returns the entity's jobs
          // across all nine flows, and the entity's OWN flow is the one whose
          // trace is worth linking. Narrowing also keeps the query on
          // `ev_enrichment_entity_idx`.
          {
            field: "content_type",
            operator: "eq",
            value: enrichmentContentTypeOf(entityType),
          },
        ],
        sorters: [],
      });
      const items = response.data as unknown as PipelineItem[];
      const match = items.find((item) => jobMatchesEntity(item, entityId!));
      if (!match) return null;
      return {
        itemId: match.id,
        source: "entity_lookup",
        runId: match.run_id,
        site: match.site,
        url: match.url ?? undefined,
        contentType: match.content_type,
      };
    },
  });

  if (staticPointer) return { pointer: staticPointer, isLoading: false };
  if (!lookupEnabled) return { pointer: null, isLoading: false };
  return { pointer: lookup.data ?? null, isLoading: lookup.isLoading };
}
